import { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../../data/config";
import { getCookies } from "cookies-next";
import BarChartIcon from "@rsuite/icons/BarChart";
import TrendIcon from "@rsuite/icons/Trend";
import CalendarIcon from "@rsuite/icons/Calendar";
import TimeIcon from "@rsuite/icons/Time";
import GrowthIcon from "@rsuite/icons/Growth";
import ReloadIcon from "@rsuite/icons/Reload";
import WarningRoundIcon from "@rsuite/icons/WarningRound";
import type { ElementType } from "react";
import SaleChart from "./SaleChart";

type StatPeriod = {
  count?: number;
  total?: number;
};

type DashboardData = {
  stats: {
    allTime: StatPeriod;
    last24h: StatPeriod;
    yesterday: StatPeriod;
    last7Days: StatPeriod;
    lastMonth: StatPeriod;
    dailyTrend: Array<{ date: string; count?: number; total?: number }>;
  };
};

const formatNumber = (value?: number) => (value ?? 0).toLocaleString("fa-IR");

type StatCardProps = {
  title: string;
  count?: number;
  total?: number;
  Icon: ElementType;
  accent: string;
  featured?: boolean;
};

function StatCard({ title, count, total, Icon, accent, featured }: StatCardProps) {
  return (
    <div
      className={[
        "rounded-xl border p-4 transition duration-200 sm:p-5",
        featured
          ? "border-slate-800 bg-slate-900 text-white shadow-md"
          : "border-slate-200/80 bg-white text-slate-900 shadow-sm hover:border-slate-300 hover:shadow-md",
      ].join(" ")}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p
            className={[
              "text-xs font-medium",
              featured ? "text-white/60" : "text-slate-500",
            ].join(" ")}
          >
            {title}
          </p>
          <p className="mt-2 text-2xl font-bold tracking-tight sm:text-[1.65rem]">
            {formatNumber(total)}
            <span
              className={[
                "ms-1 text-sm font-medium",
                featured ? "text-white/55" : "text-slate-400",
              ].join(" ")}
            >
              تومان
            </span>
          </p>
        </div>
        <div
          className={[
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
            featured ? "bg-white/10 text-white" : `${accent} text-white`,
          ].join(" ")}
        >
          <Icon className="text-[18px]" />
        </div>
      </div>
      <div
        className={[
          "flex items-center justify-between border-t pt-3 text-sm",
          featured ? "border-white/10 text-white/70" : "border-slate-100 text-slate-500",
        ].join(" ")}
      >
        <span>تعداد فروش</span>
        <span className={featured ? "font-semibold text-white" : "font-semibold text-slate-800"}>
          {formatNumber(count)}
        </span>
      </div>
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={index}
          className="h-[132px] animate-pulse rounded-xl border border-slate-200 bg-white"
        >
          <div className="flex h-full flex-col justify-between p-5">
            <div className="flex justify-between">
              <div className="h-3 w-20 rounded bg-slate-100" />
              <div className="h-10 w-10 rounded-lg bg-slate-100" />
            </div>
            <div className="h-7 w-32 rounded bg-slate-100" />
            <div className="h-3 w-full rounded bg-slate-50" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DashboardInfo() {
  const cookie = getCookies();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await axios.get(`${config.url}/v1/dashboard/dashboardinfo`, {
        headers: {
          Authorization: "Bearer " + cookie["token"],
        },
      });
      setData(response.data);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      setError(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <StatsSkeleton />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-10 text-center">
        <WarningRoundIcon className="text-2xl text-rose-500" />
        <p className="text-sm font-medium text-rose-700">بارگذاری آمار با خطا مواجه شد</p>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white transition hover:bg-slate-800"
        >
          <ReloadIcon />
          تلاش مجدد
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500">
        داده‌ای برای نمایش وجود ندارد
      </div>
    );
  }

  const { stats } = data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard
          title="فروش کلی"
          count={stats.allTime.count}
          total={stats.allTime.total}
          Icon={BarChartIcon}
          accent="bg-slate-800"
          featured
        />
        <StatCard
          title="فروش امروز"
          count={stats.last24h.count}
          total={stats.last24h.total}
          Icon={TrendIcon}
          accent="bg-emerald-600"
        />
        <StatCard
          title="فروش دیروز"
          count={stats.yesterday.count}
          total={stats.yesterday.total}
          Icon={TimeIcon}
          accent="bg-sky-600"
        />
        <StatCard
          title="فروش هفته اخیر"
          count={stats.last7Days.count}
          total={stats.last7Days.total}
          Icon={GrowthIcon}
          accent="bg-amber-600"
        />
        <StatCard
          title="فروش ماه اخیر"
          count={stats.lastMonth.count}
          total={stats.lastMonth.total}
          Icon={CalendarIcon}
          accent="bg-slate-600"
        />
      </div>

      <SaleChart newData={stats.dailyTrend} />
    </div>
  );
}
